import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Navigator from "../models/navigator";
import Map, { MapRef } from "../components/map/Map";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RGL, { Layout, WidthProvider } from "react-grid-layout";
import GridWindow from "../components/common/GridWindow";
import Camera from "../components/camera/Camera";
import CameraPano, { CameraPanoRef } from "../components/camera/CameraPano";
import Table, { TableRef } from "../components/table/Table";
import NavBarHome from "../components/navbar/NavBarHome";
import WindowIds from "../models/layout/windowIds";
import ImageStream from "../models/imageStream";
import WindowVisibility from "../models/layout/windowVisibility";
import localStorageService from "../services/localStorageService";
import LogService from "../services/logService";
import LogDirectory from "../services/logDirectory";
import imageService from "../services/imageService";
import roadService from "../services/roadService";
import ProjectInfo from "../models/projectInfo";
import tooltips from "../util/tooltips";
import appSettings from "../appSettings";
import WorkspaceManager from "../models/workspaceManager";
import User from "../models/user";
import UserContext from "../contexts/userContext";
import { useNavigate } from "react-router-dom";
import projectService from "../services/projectService";
import ProjectCollectionInfo from "../models/projectCollectionInfo";
import settingService from "../services/settingsService";
import Settings from "../models/settings";
import screenHelper from "../util/screenHelper";
import pageHelper from "../util/pageHelper";
import workspaceService from "../services/workspaceService";
import CameraPavement, {
  CameraPavementRef,
} from "../components/camera/CameraPavement";
import AppUrlParamsContext from "../contexts/appUrlParamsContext";
import mapService from "../services/mapService";
import GpsPosition from "../models/gpsPosition";
import ModalWindow from "../components/common/overlays/ModalWindow";
import NavBar from "../components/navbar/NavBar";
import BusyIndicator from "../components/common/overlays/BusyIndicator";
import WorkspaceLayout from "../models/workspaceLayout";
import chainageUnitService from "../services/chainageUnitService";
import publishService from "../services/publishService";
import PavementImageType from "../models/pavementImageType";
import SummaryChart from "../components/chart/SummaryChart";
import MapLayer from "../models/mapLayer";
import mapLayerService from "../services/mapLayerService";
import MapLayerType from "../models/mapLayerType";
import featureService from "../services/featureService";
import Feature from "../models/feature";
import ChartView from "../components/chart/Chart";
import { Chart } from "../models/chart";
import TableColum from "../models/tableColumn";
import tableService from "../services/tableService";
import chartService from "../services/chartService";
import LocationManager from "../models/locationManager";
import { createPortal } from "react-dom";
import Playback from "../components/playback/Playback";
// import TableOld from "../components/table/old/TableOld";

const logService = new LogService(LogDirectory.EnableAppLogging);

const ReactGridLayout = WidthProvider(RGL);

const Home: React.FC = () => {
  const navigator: Navigator = useMemo(() => new Navigator(), []);

  const playbackContainerRef = useRef<HTMLDivElement | null>(null);
  const playbackMainContainerRef = useRef<HTMLDivElement | null>(null);

  if (!playbackContainerRef.current) {
    // console.log("playback-created");
    playbackContainerRef.current = document.createElement("div");
  }

  const setPlaybackMainElement = useCallback(
    (element: HTMLDivElement | null) => {
      playbackMainContainerRef.current = element;

      if (!element || !playbackContainerRef.current) {
        return;
      }

      // console.log("setPlaybackMainElement");

      element.appendChild(playbackContainerRef.current);
    },
    [],
  );

  const setPlaybackElement = useCallback((element: HTMLDivElement | null) => {
    if (!element || !playbackContainerRef.current) {
      return;
    }

    // console.log("setPlaybackElement");

    element.appendChild(playbackContainerRef.current); // move, don't recreate
  }, []);

  const restorePlaybackElement = useCallback(() => {
    if (!playbackMainContainerRef.current || !playbackContainerRef.current) {
      return;
    }

    // console.log("restorePlaybackElement");

    playbackMainContainerRef.current.appendChild(playbackContainerRef.current); // move, don't recreate
  }, []);

  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [isBusyMessage, setIsBusyMessage] = useState<string>();

  const [project, setProject] = useState<ProjectInfo>(new ProjectInfo());
  const [projectCollection, setProjectCollection] =
    useState<ProjectCollectionInfo>(new ProjectCollectionInfo());

  const user = useContext<User | undefined>(UserContext);

  const [settings, setSettings] = useState<Settings>(new Settings());

  const [gridColsCount] = useState<number>(12);
  const [gridRowHeight, setGridRowHeight] = useState<number>();

  const [layout, setLayout] = useState<Layout[]>([]);
  const [windowVisibilities, setWindowVisibilities] = useState<
    WindowVisibility[]
  >([]);

  const [cameras, setCameras] = useState<ImageStream[]>([]);

  const [workspaceManager] = useState<WorkspaceManager>(new WorkspaceManager());

  const locationManagerRef = useRef<LocationManager | null>(null);

  useEffect(() => {
    locationManagerRef.current = new LocationManager(navigator, project, user);

    return () => {
      locationManagerRef.current?.detach();
      locationManagerRef.current = null;
    };
  }, [navigator, project, user]);

  const navigate = useNavigate();

  const mapRef = useRef<MapRef>(null);
  const panoCameraRef = useRef<CameraPanoRef>(null);
  const pavementCameraRef = useRef<CameraPavementRef>(null);
  const tableRef = useRef<TableRef>(null);
  const tableOldRef = useRef<TableRef>(null);

  const appUrlParams = useContext(AppUrlParamsContext);
  const [showUrlInitErrorWindow, setShowUrlInitErrorWindow] =
    useState<boolean>(false);
  const [urlInitErrorMessage, setUrlInitErrorMessage] = useState<string>("");

  const [tableColumns, setTableColumns] = useState<TableColum[]>([]);
  const [thematicMapLayers, setThematicMapLayers] = useState<MapLayer[]>([]);
  const [charts, setCharts] = useState<Chart[]>([]);

  const [oldGpsPosition, setOldGpsPosition] = useState<GpsPosition | null>(
    null,
  );

  const onProjectChanged = useCallback(
    async (project: ProjectInfo, oldProject: ProjectInfo) => {
      await navigator.setFilters(project.idProject, [], true);

      if (project !== oldProject) {
        if (navigator.gpsPosition) {
          const oldGpsPosition = navigator.gpsPosition;
          setOldGpsPosition(oldGpsPosition);
        } else {
          setOldGpsPosition(null);
        }
      }

      setProject(project);
      localStorageService.setIdProject(project.idProject);
    },
    [navigator],
  );

  useEffect(() => {
    logService.log("homePageInit-eventListenerInit");

    pageHelper.setAutoScrollbars();

    resizeGridRow();

    let isWindowResizeInProgress: boolean = false;

    const onWindowResize = (event: any) => {
      if (isWindowResizeInProgress) {
        return;
      }

      isWindowResizeInProgress = true;

      logService.log("window:onWindowResize");

      setTimeout(() => {
        onResize();
        resizeGridRow();
        isWindowResizeInProgress = false;
      }, 500);
    };

    window.addEventListener("resize", onWindowResize);

    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  useEffect(() => {
    console.log("homePageInit-initProject");

    const init = async () => {
      let projectCollection: ProjectCollectionInfo | undefined = undefined;
      let project: ProjectInfo | undefined = undefined;

      const { data: projectCollections } =
        await projectService.getProjectCollectionsInfos();

      if (appUrlParams.hasUrlParams) {
        console.log("homePageInit-initProject-usingUrlPramaters");

        let projectFound = false;

        for (const prjCollection of projectCollections) {
          for (const prj of prjCollection.projects) {
            if (
              prj.projectCode.toLowerCase() ===
              appUrlParams.project.toLowerCase()
            ) {
              projectFound = true;

              projectCollection = prjCollection;
              project = prj;
              break;
            }
          }
          if (projectFound) {
            break;
          }
        }

        if (!projectFound) {
          showInitErrorWindow(
            "Cannot initialize in the project specified in the url.",
          );

          // flow is continued in onInitErrorWindowClose()

          return;
        }
      } else {
        let idProjectCollection = localStorageService.getIdProjectCollection();

        if (!idProjectCollection) {
          navigate("/projects");
          return;
        }

        const prjCollection = projectCollections.find(
          (c) => c.idProjectCollection === idProjectCollection,
        );

        if (!prjCollection || prjCollection.projects.length === 0) {
          navigate("/projects");
          return;
        }

        projectCollection = prjCollection;

        let idProject = localStorageService.getIdProject();
        if (idProject) {
          project = prjCollection.projects.find(
            (p) => p.idProject === idProject,
          );
        }

        if (!project) {
          project = prjCollection.projects[0];
          localStorageService.setIdProject(project.idProject);
        }
      }

      if (!projectCollection || !project) {
        navigate("/projects");
        return;
      }

      if (project.isExpired && !User.canAccessExpiredProject(user)) {
        navigate("/projects");
        return;
      }

      if (project.projectCode === "dfwairportcollection") {
        navigate("/airport");
        return;
      }

      if (project.projectCode === "lidarcollection") {
        navigate("/lidar");
        return;
      }

      setProjectCollection(projectCollection);
      setProject(project);
    };

    init();

    return () => {
      navigator.navigatorPosition = null;
    };
  }, [navigator, navigate, appUrlParams, user]);

  const initBeforeSettingLayout = useCallback(async () => {
    // console.log("initBeforeSettingLayout");
    const tableColumns = await initTableColumns(project);
    await initCharts(project);
    await initMapLayers(project, tableColumns);
  }, [project]);

  const initAfterSettingLayout = useCallback(async () => {
    // console.log("initAfterSettingLayout");
    tableRef.current?.refresh();
    tableOldRef.current?.refresh();
  }, []);

  useEffect(() => {
    let isDisposed = false;

    if (project.idProject === 0 || !user) {
      return;
    }

    console.log("homePageInit-initLocation");

    const initNavigatorPosition = async (navigator: Navigator) => {
      // console.log("homePageInit-initNavigatorPosition");

      if (oldGpsPosition) {
        // project changed / year changed - try to keep old project gps position
        // console.log(
        //   "homePageInit-initNavigatorPosition-oldGpsPosition",
        //   oldGpsPosition,
        // );

        const { data: navigatorPosition } =
          await mapService.getClosestNavigatorPositionForGpsPosition(
            project.idProject,
            oldGpsPosition,
            7,
            [],
          );

        if (navigatorPosition) {
          navigator.move(navigatorPosition);
          return;
        }
      }

      let { data: projectSettings } = await settingService.getProjectSettings(
        project.idProject,
      );

      if (projectSettings.location) {
        // console.log(
        //   "homePageInit-initNavigatorPosition-projectSettings.location",
        //   projectSettings.location,
        // );

        navigator.move(projectSettings.location);
        return;
      }

      // console.log("homePageInit-initNavigatorPosition-defaultLocation");

      let { data: defaultLocation } =
        await roadService.getProjectDefaultLocation(project.idProject);

      navigator.move(defaultLocation);
    };

    const onSettingsChanged = async (settings: Settings) => {
      if (user?.isDemoUser) {
        return;
      }

      await settingService.saveSettings(settings);
    };

    let publishInProgressTimerId: any;
    let isPublishInProgress: boolean = false;

    const init = async () => {
      if (project.isPublished) {
        publishInProgressTimerId = setInterval(async () => {
          const { data: isPublishInProgressLive } =
            await publishService.isPublishInProgress(project.projectCode);

          const isChanged = isPublishInProgress !== isPublishInProgressLive;

          if (isChanged) {
            isPublishInProgress = isPublishInProgressLive;

            if (isPublishInProgressLive) {
              setIsBusy(true);
              setIsBusyMessage(
                "New Data Publishing in Progress... Please wait...",
              );
            } else {
              setIsBusy(false);
              setIsBusyMessage("");

              if (project.idProject) {
                const { data: refreshProjectCollections } =
                  await projectService.getProjectCollectionsInfos();

                const projectRefresh = getProjectFromProjectCollections(
                  project.idProject,
                  refreshProjectCollections,
                );

                if (projectRefresh) {
                  await onProjectChanged(projectRefresh, projectRefresh);
                }
              }
            }
          }
        }, 5000);
      }

      const getProjectFromProjectCollections = (
        idProject: number,
        projectCollections: ProjectCollectionInfo[],
      ) => {
        for (const projectCollection of projectCollections) {
          for (const project of projectCollection.projects) {
            if (project.idProject === idProject) {
              return project;
            }
          }
        }

        return undefined;
      };

      const defaultSettings = new Settings();

      defaultSettings.showChainage = project.showChainageDefault;

      defaultSettings.showPavementDistress = true;
      defaultSettings.showPavementDistressBoxes = false;
      defaultSettings.showPavementDistressLabels = false;
      defaultSettings.showPavementLaneMarkings = true;
      defaultSettings.showPavementEvents = true;
      defaultSettings.showPavementMarkingsDuringPlayback = false;

      defaultSettings.pavementImageType = PavementImageType.Image3D;

      const settings = await settingService.getSettings(defaultSettings);

      if (!settings.pavementImageType) {
        settings.pavementImageType = defaultSettings.pavementImageType;
      }

      setSettings(settings);

      settings.onSettingsChanged = onSettingsChanged;

      workspaceManager.clear();

      workspaceManager.setUser(user);

      let imageStreams: ImageStream[] = [];

      if (project.hasData) {
        const { data: imgs } = await imageService.getImageStreams(
          project.idProject,
        );

        imageStreams = imgs;

        setCameras(imageStreams);
      }

      await initBeforeSettingLayout();

      await workspaceManager.initWorkspace(imageStreams, project.idProject);
      setLayout(workspaceManager.layout);

      if (!project.hasData) {
        for (const visibility of workspaceManager.windowVisibilities) {
          visibility.isVisibile = false;
        }
      }

      setWindowVisibilities(workspaceManager.windowVisibilities);

      await initAfterSettingLayout();

      tooltips.initTooltips();

      if (!project.hasData) {
        return;
      }

      if (isDisposed) {
        return;
      }

      if (appUrlParams.hasUrlParams) {
        const isBusyMessage =
          "Initializing using URL parameters... Please wait...";

        if (appUrlParams.hasLatLon()) {
          const gpsPosition = new GpsPosition();
          gpsPosition.latitude = appUrlParams.latitude;
          gpsPosition.longitude = appUrlParams.longitude;

          setIsBusy(true);
          setIsBusyMessage(isBusyMessage);

          const { data: navigatorPosition } =
            await mapService.getClosestNavigatorPositionForGpsPosition(
              project.idProject,
              gpsPosition,
              7,
              [],
            );

          setIsBusy(false);
          setIsBusyMessage(undefined);

          if (navigatorPosition) {
            navigator.move(navigatorPosition);
          } else {
            showInitErrorWindow(
              "Cannot initialize in the gps position specified in the url.",
            );
            initNavigatorPosition(navigator);
          }
        } else if (
          appUrlParams.hasNetworkPosition &&
          appUrlParams.networkPosition
        ) {
          const networkPosition = { ...appUrlParams.networkPosition };
          networkPosition.road = decodeURI(networkPosition.road);
          networkPosition.direction = decodeURI(networkPosition.direction);
          networkPosition.segmentId = decodeURI(networkPosition.segmentId);
          networkPosition.shapeId = decodeURI(networkPosition.shapeId);
          networkPosition.chainage = chainageUnitService.covertChaingeToMeters(
            networkPosition.chainage,
            project,
          );

          setIsBusy(true);
          setIsBusyMessage(isBusyMessage);

          const { data: navigatorPosition } =
            await roadService.getNavigatorPositionForNetworkLocation(
              project.idProject,
              networkPosition,
            );

          setIsBusy(false);
          setIsBusyMessage(undefined);

          if (navigatorPosition) {
            navigator.move(navigatorPosition);
          } else {
            showInitErrorWindow(
              "Cannot initialize in the network position specified in the url.",
            );
            initNavigatorPosition(navigator);
          }
        } else {
          initNavigatorPosition(navigator);
        }

        appUrlParams.clear();
      } else {
        initNavigatorPosition(navigator);
      }
    };

    init();

    return () => {
      isDisposed = true;

      if (publishInProgressTimerId) {
        clearInterval(publishInProgressTimerId);
      }
    };
  }, [
    navigator,
    project,
    user,
    workspaceManager,
    appUrlParams,
    setIsBusy,
    onProjectChanged,
    oldGpsPosition,
    initBeforeSettingLayout,
    initAfterSettingLayout,
  ]);

  const initTableColumns = async (project: ProjectInfo) => {
    const { data: tableColumns } = await tableService.getTableColumns(
      project.idProject,
    );

    setTableColumns(tableColumns);

    return tableColumns;
  };

  const initCharts = async (project: ProjectInfo) => {
    const { data: charts } = await chartService.getCharts(project.idProject);

    setCharts(charts);
  };

  const initMapLayers = async (
    project: ProjectInfo,
    tableColumns: TableColum[],
  ) => {
    // console.log("initThematicMaps");

    const mapLayers = await mapLayerService.getMapLayers(
      project.idProject,
      tableColumns,
    );

    const thematicMapLayers = mapLayers.filter(
      (ml) => ml.mapLayerType === MapLayerType.ThematicLayer,
    );

    setThematicMapLayers(thematicMapLayers);
  };

  const onMapLayersChanged = async () => {
    await initMapLayers(project, tableColumns);
  };

  const onChartsChanged = async (
    chart: Chart,
    isNew: boolean,
    isUpdated: boolean,
    isDeleted: boolean,
    oldChart: Chart | undefined,
  ) => {
    const prevLayout = [...workspaceManager.layout];
    const prevLayoutInvisibleItems = [...workspaceManager.layoutInvisibleItems];

    // reload charts

    if (!user?.isDemoUser) {
      // console.log("initCharts");
      await initCharts(project);
    } else {
      if (isNew) {
        const newCharts = [...charts];
        newCharts.push(chart);
        setCharts(newCharts);
      } else if (isDeleted) {
        const newCharts = charts.filter((c) => c.chartName !== chart.chartName);
        setCharts(newCharts);
      } else {
        const newCharts = charts.map((c) => {
          if (c.chartName === chart.chartName) {
            return chart;
          } else {
            return c;
          }
        });
        setCharts(newCharts);
      }
    }

    // update layout

    setTimeout(() => {
      // console.log("afterUpdateUi"); // after charts list was rendered and layout updated

      if (isNew) {
        // show chart on create - old mechaism (replaced by chart.isVisible)
        // const windowId = WindowIds.getChartId(chart.chartName);
        // onWindowVisibilityChanged(windowId, true);
      } else if (isUpdated) {
        if (oldChart) {
          const newWindowId = WindowIds.getChartId(chart.chartName);
          const oldWindowId = WindowIds.getChartId(oldChart.chartName);

          if (newWindowId !== oldWindowId) {
            const prevLayoutItem = prevLayout.find((l) => l.i === oldWindowId);
            if (prevLayoutItem) {
              prevLayoutItem.i = newWindowId;
            }

            const prevLayoutInvisibleItem = prevLayoutInvisibleItems.find(
              (l) => l.i === oldWindowId,
            );
            if (prevLayoutInvisibleItem) {
              prevLayoutInvisibleItem.i = newWindowId;
            }

            const newWorkspaceLayout = new WorkspaceLayout();
            newWorkspaceLayout.layout = prevLayout;
            newWorkspaceLayout.layoutInvisibleItems = prevLayoutInvisibleItems;

            onSetLayout(newWorkspaceLayout);
          }
        }
      }
    }, 100);
  };

  const onInitErrorWindowClose = () => {
    setShowUrlInitErrorWindow(false);
    setUrlInitErrorMessage("");

    appUrlParams.clear();

    if (project.idProject === 0) {
      navigate("/projects");
    }
  };

  const showInitErrorWindow = (errorMessage: string) => {
    setUrlInitErrorMessage(errorMessage);
    setShowUrlInitErrorWindow(true);
  };

  const onWindowVisibilityChanged = (
    windowId: string,
    isVisible: boolean,
    sender?: {
      chart?: Chart;
    },
  ) => {
    workspaceManager.onWindowVisibilityChanged(windowId, isVisible);
    setLayout(workspaceManager.layout);
    setWindowVisibilities(workspaceManager.windowVisibilities);

    const chart = sender?.chart;

    if (chart) {
      const newCharts = [...charts];
      const existingChart = newCharts.find((c) => c.idChart === chart.idChart);
      if (existingChart) {
        existingChart.isVisible = isVisible;
        setCharts(newCharts);
      }

      if (!user?.isDemoUser) {
        chartService.updateChartVisibility(chart.idChart, isVisible);
      }
    }
  };

  const onGridResizeStart = (layout: Layout[]) => {
    logService.log("layout:onGridResizeStart");
  };

  const onGridResizeStop = (layout: Layout[]) => {
    logService.log("layout:onGridResizeStop");

    if (cameras.find((c) => c.isPanoramic)) {
      resizeCameraPano(layout);
    }

    onResize();
  };

  const resizeCameraPano = (newLayout: Layout[]) => {
    const panoCameraName = cameras.find((c) => c.isPanoramic)?.cameraName || "";
    const id = WindowIds.getCameraId(panoCameraName);

    const newPanoLayout = newLayout.find((i) => i.i === id);
    const oldPanoLayout = layout.find((i) => i.i === id);

    if (newPanoLayout && oldPanoLayout) {
      const isPanoLayoutSizeChanged =
        oldPanoLayout.w !== newPanoLayout.w ||
        oldPanoLayout.h !== newPanoLayout.h;
      if (isPanoLayoutSizeChanged) {
        panoCameraRef?.current?.refresh();
      }
    }
  };

  const resizeGridRow = () => {
    if (screenHelper.is4k()) {
      setGridRowHeight(100);
    } else {
      setGridRowHeight(97);
    }
  };

  const onResize = () => {
    tableRef?.current?.refresh();
    tableOldRef?.current?.refresh();
    mapRef?.current?.refresh();
    pavementCameraRef?.current?.refresh();
  };

  const onLayoutChanged = (layout: Layout[]) => {
    // console.log("layout:onLayoutChanged", layout);
    workspaceManager.onLayoutChanged(layout);
    setLayout(workspaceManager.layout);
  };

  const getWindowVisibility = (
    windowId: string,
  ): WindowVisibility | undefined => {
    return windowVisibilities.find((wv) => wv.windowId === windowId);
  };

  const onResetLayout = async () => {
    await workspaceManager.resetWorkspace(cameras, project.idProject);
    setLayout(workspaceManager.layout);
    setWindowVisibilities(workspaceManager.windowVisibilities);
  };

  const onSetLayout = async (layout: WorkspaceLayout) => {
    // console.log("onSetLayout");
    workspaceManager.setLayout(layout, cameras);
    setLayout(workspaceManager.layout);
    setWindowVisibilities(workspaceManager.windowVisibilities);

    await initAfterSettingLayout();
  };

  const onSetProjectDefaultLayout = async () => {
    await workspaceService.setProjectDefaultWorkspace(
      workspaceManager.workspace,
    );
  };

  const onSetProjectDefaultLocation = async () => {
    if (!navigator.navigatorPosition) {
      return;
    }

    await roadService.setProjectDefaultLocation(
      project.idProject,
      navigator.navigatorPosition,
    );
  };

  if (project.idProject === 0) {
    return (
      <>
        <ToastContainer />
        <div className="version">{appSettings.appVersion}</div>
        <NavBar />
        {showUrlInitErrorWindow && (
          <ModalWindow
            title="URL Initialization"
            onOk={() => onInitErrorWindowClose()}
            hasCancel={false}
          >
            <div>{urlInitErrorMessage}</div>
          </ModalWindow>
        )}
      </>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="version">{appSettings.appVersion}</div>
      {playbackContainerRef.current &&
        createPortal(
          <Playback
            navigator={navigator}
            project={project}
            cameras={cameras}
            settings={settings}
          />,
          playbackContainerRef.current,
        )}
      <NavBarHome
        navigator={navigator}
        project={project}
        projectCollection={projectCollection}
        cameras={cameras}
        charts={charts}
        thematicMapLayers={thematicMapLayers}
        windowVisibilities={windowVisibilities}
        onWindowVisibilityChanged={onWindowVisibilityChanged}
        onYearChanged={onProjectChanged}
        settings={settings}
        onResetLayout={onResetLayout}
        onSetLayout={onSetLayout}
        onSetProjectDefaultLayout={onSetProjectDefaultLayout}
        onSetProjectDefaultLocation={onSetProjectDefaultLocation}
        onChartsChanged={onChartsChanged}
        setPlaybackElement={setPlaybackMainElement}
      />
      {!project.hasData && (
        <div
          className="busy-indicator-text"
          style={{
            textAlign: "center",
            padding: "100px",
            fontWeight: "bold",
          }}
        >
          No Data
        </div>
      )}
      <div id="content">
        <ReactGridLayout
          className="layout"
          layout={layout}
          cols={gridColsCount}
          rowHeight={gridRowHeight}
          draggableHandle=".grid-window-title"
          onResizeStart={onGridResizeStart}
          onResizeStop={onGridResizeStop}
          onLayoutChange={onLayoutChanged}
          margin={[10, 10]}
          useCSSTransforms={false}
        >
          {featureService.isFeatureEnabled(Feature.Charts, project) &&
            charts.map(
              (chart) =>
                chart.isVisible && (
                  <div
                    key={WindowIds.getChartId(chart.chartName)}
                    data-grid={{ x: 0, y: 0, w: 3, h: 3 }}
                  >
                    <GridWindow
                      windowId={WindowIds.getChartId(chart.chartName)}
                      title={`${chart.chartName} Chart`}
                      onClose={(windowId: string) =>
                        onWindowVisibilityChanged(windowId, false, { chart })
                      }
                    >
                      <ChartView
                        chart={chart}
                        tableColumns={tableColumns}
                        navigator={navigator}
                        project={project}
                        settings={settings}
                      />
                    </GridWindow>
                  </div>
                ),
            )}
          {featureService.isFeatureEnabled(Feature.SummaryCharts, project) &&
            thematicMapLayers.map(
              (thematicMapLayer) =>
                getWindowVisibility(
                  WindowIds.getSummaryChartId(thematicMapLayer.layerName),
                )?.isVisibile && (
                  <div
                    key={WindowIds.getSummaryChartId(
                      thematicMapLayer.layerName,
                    )}
                    data-grid={{ x: 0, y: 0, w: 3, h: 3 }}
                  >
                    <GridWindow
                      windowId={WindowIds.getSummaryChartId(
                        thematicMapLayer.layerName,
                      )}
                      title={`${thematicMapLayer.layerName} Summary Chart`}
                      onClose={(windowId: string) =>
                        onWindowVisibilityChanged(windowId, false)
                      }
                    >
                      <SummaryChart
                        mapLayer={thematicMapLayer}
                        navigator={navigator}
                        project={project}
                      />
                    </GridWindow>
                  </div>
                ),
            )}
          {cameras.map(
            (camera) =>
              getWindowVisibility(WindowIds.getCameraId(camera.cameraName))
                ?.isVisibile && (
                <div key={WindowIds.getCameraId(camera.cameraName)}>
                  <GridWindow
                    windowId={WindowIds.getCameraId(camera.cameraName)}
                    title={`Camera ${camera.cameraName}`}
                    onClose={(windowId: string) =>
                      onWindowVisibilityChanged(windowId, false)
                    }
                  >
                    {!camera.isPanoramic && !camera.isPavement && (
                      <Camera
                        cameraName={camera.cameraName}
                        navigator={navigator}
                        project={project}
                        settings={settings}
                      />
                    )}
                    {camera.isPavement && (
                      <CameraPavement
                        ref={pavementCameraRef}
                        cameraName={camera.cameraName}
                        navigator={navigator}
                        project={project}
                        settings={settings}
                        setPlaybackElement={setPlaybackElement}
                        restorePlaybackElement={restorePlaybackElement}
                      />
                    )}
                    {camera.isPanoramic && (
                      <CameraPano
                        ref={panoCameraRef}
                        cameraName={camera.cameraName}
                        navigator={navigator}
                        project={project}
                        setPlaybackElement={setPlaybackElement}
                        restorePlaybackElement={restorePlaybackElement}
                      />
                    )}
                  </GridWindow>
                </div>
              ),
          )}
          {getWindowVisibility(WindowIds.MapId)?.isVisibile && (
            <div key={WindowIds.MapId}>
              <GridWindow
                windowId={WindowIds.MapId}
                title="Map"
                onClose={(windowId: string) =>
                  onWindowVisibilityChanged(windowId, false)
                }
              >
                <Map
                  ref={mapRef}
                  navigator={navigator}
                  project={project}
                  onMapLayersChanged={onMapLayersChanged}
                />
              </GridWindow>
            </div>
          )}
          {getWindowVisibility(WindowIds.TableId)?.isVisibile && (
            <div key={WindowIds.TableId}>
              <GridWindow
                windowId={WindowIds.TableId}
                title="Roads"
                onClose={(windowId: string) =>
                  onWindowVisibilityChanged(windowId, false)
                }
              >
                <Table
                  ref={tableRef}
                  navigator={navigator}
                  project={project}
                  settings={settings}
                />
              </GridWindow>
            </div>
          )}
          {/* <div key={WindowIds.TableOldId}>
            <GridWindow
              windowId={WindowIds.TableOldId}
              title="RoadsOld"
              onClose={(windowId: string) =>
                onWindowVisibilityChanged(windowId, false)
              }
            >
              <TableOld
                ref={tableOldRef}
                navigator={navigator}
                project={project}
                settings={settings}
              />
            </GridWindow>
          </div> */}
        </ReactGridLayout>
        {showUrlInitErrorWindow && (
          <ModalWindow
            title="URL Initialization"
            onOk={() => onInitErrorWindowClose()}
            hasCancel={false}
          >
            <div>{urlInitErrorMessage}</div>
          </ModalWindow>
        )}
        {isBusy && <BusyIndicator isBusyMessage={isBusyMessage} />}
      </div>
    </>
  );
};

export default Home;
